import React, { createContext, useContext, ReactNode, useMemo } from 'react';
import { EntityRecord } from '@/types/entity';
import { useSwivuNotes, TUseSwivuNotesReturn } from '@/react/hooks/entityHooks/useSwivuNotes';
import { useEntityManagerContext } from '@/react/components/entityManager/context/EntityManagerContext';

interface EntityNotesContextValue extends TUseSwivuNotesReturn {
  entityRecord: EntityRecord;
}

const EntityNotesContext = createContext<EntityNotesContextValue | undefined>(undefined);

interface EntityNotesProviderProps {
  children: ReactNode;
}

export const EntityNotesProvider: React.FC<EntityNotesProviderProps> = ({ children }) => {
  const { record } = useEntityManagerContext();
  const notesOperations = useSwivuNotes({ entityRecord: record });

  const contextValue = useMemo(
    () => ({
      ...notesOperations,
      entityRecord: record
    }),
    [notesOperations, record]
  );

  return <EntityNotesContext.Provider value={contextValue}>{children}</EntityNotesContext.Provider>;
};

export const useEntityNotesContext = (): EntityNotesContextValue => {
  const context = useContext(EntityNotesContext);
  if (!context) {
    throw new Error('useEntityNotesContext must be used within EntityNotesProvider');
  }
  return context;
};

export const useEntityNotesContextOptional = (): EntityNotesContextValue | undefined => {
  return useContext(EntityNotesContext);
};

export type { EntityNotesContextValue };